/**
 * Dedicated, isolated agent chat session for the onboarding flow.
 *
 * Onboarding's scripted steps send pre-written prompts to the canvas
 * agent. If they reused whatever chat the user already had open, the
 * prompt would land in a pre-existing conversation and the agent would
 * be confused by the prior context (Sentry USER-DASHBOARD-2SX).
 *
 * This hook gives onboarding its own flow session, created with the
 * `CANVAS_AGENT_ONBOARDING` template (geared toward simulating action
 * for the scripted beats) rather than the general `CANVAS_AGENT`. The
 * session id is persisted on the onboarding state-shell node's `data`
 * (`OnboardingData.onboardingSessionId`) so it survives a mid-flow
 * reload and is shared across the agent-chat steps (5 and 7). The
 * session is activated directly via `openSession(id, "replace")`, which
 * swaps the floating-chat stack top regardless of whether the user
 * already has a chat open.
 *
 * When onboarding finishes, `deleteOnboardingAgentSession()` tears the
 * session down so the user never lands in a chat with the onboarding
 * agent after the tour is over.
 */

import { orgAPI } from "@/api/api";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
} from "@/api/flow_sessions";
import Sentry from "@/integrations/Sentry";
import { useCanvasApi } from "@/pages/canvas/context/CanvasContext";
import { useCanvasFloatingChat } from "@/pages/canvas/context/CanvasFloatingChatContext";
import { useCanvasRoute } from "@/pages/canvas/context/CanvasRouteContext";
import { FLOW_SESSION_MODES, type FlowSession } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import type { OnboardingData } from "../shared/types";

/**
 * Returns:
 *  - `openOnboardingAgentSession()` — creates (once) and opens the
 *    dedicated onboarding chat session, persisting its id onto the
 *    onboarding node so later steps reuse it instead of duplicating.
 *  - `deleteOnboardingAgentSession()` — closes the floating chat and
 *    deletes the session; called when onboarding finishes.
 *
 * @param id   onboarding state-shell node id (for `updateNodeData`)
 * @param data current `OnboardingData` (read for `onboardingSessionId`)
 */
export function useOnboardingAgentSession(id: string, data: OnboardingData) {
  const { canvasId } = useCanvasRoute();
  const canvas = useCanvasApi();
  const { openSession, closeChat } = useCanvasFloatingChat();
  const createFlowSession = useCreateFlowSession();
  const deleteFlowSession = useDeleteFlowSession();
  const queryClient = useQueryClient();

  // Latest persisted session id, read through a ref so the returned
  // callbacks stay referentially stable across step re-renders.
  const sessionIdRef = useRef(data.onboardingSessionId);
  sessionIdRef.current = data.onboardingSessionId;

  // Dedupe concurrent calls (e.g. an effect re-running before the Loro
  // write of `onboardingSessionId` round-trips) so we never mint two.
  const inFlightRef = useRef<Promise<void> | null>(null);

  const openOnboardingAgentSession = useCallback((): Promise<void> => {
    if (!canvasId) return Promise.resolve();
    if (inFlightRef.current) return inFlightRef.current;

    const run = async () => {
      const existing = sessionIdRef.current;
      if (existing) {
        // Confirm the persisted session still exists. Prefer the query
        // cache; fall back to an authoritative fetch on a cold cache
        // (e.g. after a mid-flow reload) so we don't create a duplicate.
        let sessions = queryClient.getQueryData<FlowSession[]>([
          "canvas-flow-sessions",
          canvasId,
        ]);
        if (!sessions) {
          try {
            sessions = await orgAPI.canvas.getFlowSessions(canvasId);
          } catch {
            sessions = undefined;
          }
        }
        if (sessions?.some((s) => s.id === existing)) {
          openSession(existing, "replace");
          return;
        }
        // Persisted id no longer resolves to a live session — fall
        // through and mint a fresh one.
      }

      try {
        // No harness: onboarding is scripted and its composer is `editingLocked`, so there is
        // no selector to read. It runs on cohesive, as the script was written against.
        const session = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_ONBOARDING,
          class: "canvas",
          canvas_id: canvasId,
        });
        sessionIdRef.current = session.id;
        canvas.updateNodeData(id, { onboardingSessionId: session.id });
        openSession(session.id, "replace");
      } catch (err) {
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error("Failed to open onboarding chat"),
        );
        toast.error("Failed to open chat");
      }
    };

    const promise = run().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = promise;
    return promise;
  }, [
    canvasId,
    canvas,
    id,
    openSession,
    createFlowSession.mutateAsync,
    queryClient,
  ]);

  const deleteOnboardingAgentSession = useCallback(async (): Promise<void> => {
    const existing = sessionIdRef.current;
    // Close the floating chat first so it doesn't keep rendering a
    // session that's about to be deleted.
    closeChat();
    if (!existing) return;
    sessionIdRef.current = undefined;
    try {
      await deleteFlowSession.mutateAsync(existing);
    } catch (err) {
      // Best-effort: an already-deleted / 404 session must not block
      // finishing onboarding.
      Sentry.captureException(
        err instanceof Error
          ? err
          : new Error("Failed to delete onboarding chat session"),
      );
    }
    if (canvasId) {
      queryClient.invalidateQueries({
        queryKey: ["canvas-flow-sessions", canvasId],
      });
    }
    queryClient.removeQueries({ queryKey: ["flowSession", existing] });
  }, [closeChat, deleteFlowSession.mutateAsync, canvasId, queryClient]);

  return { openOnboardingAgentSession, deleteOnboardingAgentSession };
}
